import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import CareerList from '../components/careers/CareerList';
import DevelopmentGoals from '../components/careers/DevelopmentGoals';
import ActionPlan from '../components/careers/ActionPlan';
import { useTeam } from '../contexts/TeamContext';
import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

export default function Careers() {
  const [selectedMenu, setSelectedMenu] = useState(() => {
    return localStorage.getItem('selectedCareerMenu') || 'development-goals';
  });

  // Add this effect to persist menu selection
  useEffect(() => {
    localStorage.setItem('selectedCareerMenu', selectedMenu);
  }, [selectedMenu]);

  const [careerData, setCareerData] = useState(null);
  const { selectedMember } = useTeam();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'development-goals');
  const [selectedGoalId, setSelectedGoalId] = useState(null);

  // Fetch career data when selected member changes
  useEffect(() => {
    console.log('Selected member changed:', selectedMember?.name); // Debug log

    const fetchCareerData = async () => {
      if (!currentUser || !selectedMember) {
        console.log('No current user or selected member'); // Debug log
        setCareerData(null);
        return;
      }

      try {
        console.log('Fetching career data for:', selectedMember.name); // Debug log
        const careerRef = doc(db, 'careers', selectedMember.id);
        const careerDoc = await getDoc(careerRef);

        if (careerDoc.exists()) {
          console.log('Career data found:', careerDoc.data()); // Debug log
          setCareerData(careerDoc.data());
        } else {
          console.log('Creating new career data for:', selectedMember.name); // Debug log
          const initialCareerData = {
            actionPlan: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            managerId: currentUser.uid,
            memberId: selectedMember.id,
            careerGoal: '',
            nextRole: '',
            targetYear: 1,
            developmentGoals: [],
            currentCapabilities: [],
            developmentAreas: []
          };

          await setDoc(careerRef, initialCareerData);
          setCareerData(initialCareerData);
        }
      } catch (error) {
        console.error('Error fetching career data:', error);
        setCareerData(null);
      }
    };

    fetchCareerData();
  }, [currentUser, selectedMember]); // Dependencies that trigger the effect

  useEffect(() => {
    // Handle navigation state
    if (location.state?.activeTab) {
      setSelectedMenu(location.state.activeTab);
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const handleMenuClick = (menu) => {
    setSelectedMenu(menu);
    localStorage.setItem('selectedCareerMenu', menu);
  };

  const handleAddActionItem = (goalId) => {
    setSelectedMenu('action-plan');
    // Use setTimeout to ensure the ActionPlan component is mounted
    setTimeout(() => {
      const goalElement = document.getElementById(`goal-${goalId}`);
      if (goalElement) {
        goalElement.scrollIntoView({ behavior: 'smooth' });
        const inputElement = goalElement.querySelector('input[type="text"]');
        if (inputElement) {
          inputElement.focus();
        }
      }
    }, 100);
  };

  const handleCareerDataUpdate = async (updatedData) => {
    if (!selectedMember) return;

    try {
      console.log('Updating career data for:', selectedMember.name); // Debug log
      const careerRef = doc(db, 'careers', selectedMember.id);
      const updateObj = {
        ...careerData, // Preserve existing data
        ...updatedData, // Apply updates
        updatedAt: new Date()
      };

      await updateDoc(careerRef, updateObj);
      setCareerData(updateObj);
      console.log('Career data updated successfully:', updateObj); // Debug log
    } catch (error) {
      console.error('Error updating career data:', error);
    }
  };

  const handleGoToGoal = (goalId) => {
    setSelectedMenu('development-goals');
    setSelectedGoalId(goalId); // This will be used to open the drawer
  };

  // Debug log for render
  console.log('Rendering CareerPlanning with data:', { 
    selectedMember: selectedMember?.name,
    careerData: careerData
  });

  if (!selectedMember) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">
          Please select a team member to view their career planning
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%', px:2 }}>
      <Box sx={{ width: 300, pt: 0, pr: 2, pb: 2, pl: 2, py:3 }}>
        <CareerList 
          onMenuSelect={handleMenuClick} 
          selectedMenu={selectedMenu}
        />
      </Box>

      <Box sx={{ flex: 1, p: 5, pr: 3, pb: 3, pl: 3 }}>
        {selectedMenu === 'development-goals' ? (
          <DevelopmentGoals 
            careerData={careerData} 
            onUpdate={handleCareerDataUpdate}
            selectedMember={selectedMember}
            onAddActionItem={handleAddActionItem}
            selectedGoalId={selectedGoalId}
            onGoalSelected={() => setSelectedGoalId(null)}
          />
        ) : (
          <ActionPlan 
            careerData={careerData} 
            onUpdate={handleCareerDataUpdate}
            onGoToGoal={handleGoToGoal}
          />
        )}
      </Box>
    </Box>
  );
}